import { Button, Kind } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import Counter from 'components/Counter'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useCallback, useMemo } from 'react'
import { StyleObject } from 'styletron-standard'
import type { ConsolidatedLocation } from './mockLocations'

const t = buildTranslate('locations.location_list.health_summary')

// Phase 2 fleet-wide health summary. Reads real merchant-api data via
// the `locations` prop the parent scene already has — this is a design
// preview (awaiting Figma) but not a data mock. Post Reno review
// (2026-05-12):
//   - errors/notices tiles are now clickable filters, reversing an earlier
//     read-only decision now that operators have asked to jump straight
//     from "how many" to "which ones"
//   - all three tiles always render, even at zero
//   - section title uses the same Header4 + Title Case treatment as the
//     other OC section titles (Detail page schedule overrides, menu
//     lists) — no uppercase, no Lato override
// Built from the existing `Counter` primitive composed in a flex row
// with a 4px coloured left-accent matching the per-row badge palette.

type Bucket = 'healthy' | 'errors' | 'notices'

const HEADING_ID = 'oc-health-summary-heading'

const accentFor = (bucket: Bucket): string => {
  if (bucket === 'errors') return palette.cayenne50
  if (bucket === 'notices') return palette.eggYolk50
  // `nephritis` is the unsuffixed canonical green — the palette only
  // defines `nephritis` / `nephritis10` / `nephritis20`, so this IS the
  // 50-equivalent for the green family.
  return palette.nephritis
}

// Selected-state background tint, one shade lighter than `accentFor`.
// `healthy` is never clickable/selected, so its branch is unreachable —
// returned only to keep this total like `accentFor`.
const tintFor = (bucket: Bucket): string => {
  if (bucket === 'errors') return palette.cayenne10
  if (bucket === 'notices') return palette.eggYolk10
  return palette.white
}

// `healthy` is never clickable, so its branch is unreachable — returned
// only to keep this total like `accentFor`/`tintFor`.
const ariaLabelFor = (bucket: Bucket): string => {
  if (bucket === 'errors') return t('filter_by_errors')
  if (bucket === 'notices') return t('filter_by_notices')
  return ''
}

const Tile = ({
  bucket,
  count,
  label,
  isSelected,
  onClick,
}: {
  bucket: Bucket
  count: number
  label: string
  isSelected?: boolean
  onClick?: () => void
}) => {
  const [css] = useStyletron()
  const baseTileStyle: StyleObject = {
    flex: '1 0 0',
    minWidth: 0,
    borderTop: `1px solid ${palette.grey20}`,
    borderRight: `1px solid ${palette.grey20}`,
    borderBottom: `1px solid ${palette.grey20}`,
    borderLeft: `4px solid ${accentFor(bucket)}`,
    borderRadius: '4px',
  }
  const content = (
    <Counter primaryValue={count.toLocaleString()} name={label} height="40px" />
  )

  if (onClick) {
    return (
      <Button
        kind={Kind.MINIMAL}
        onClick={onClick}
        aria-pressed={isSelected}
        aria-label={ariaLabelFor(bucket)}
        overrides={{
          BaseButton: {
            style: {
              ...baseTileStyle,
              padding: '24px',
              backgroundColor: isSelected ? tintFor(bucket) : palette.white,
              ':hover': {
                backgroundColor: isSelected ? tintFor(bucket) : palette.white,
              },
            },
          },
        }}
      >
        {content}
      </Button>
    )
  }

  const divClassName = `p-m ${css({
    ...baseTileStyle,
    backgroundColor: palette.white,
  })}`
  return <div className={divClassName}>{content}</div>
}

type Props = {
  locations: ConsolidatedLocation[]
  activeBucket: 'errors' | 'notices' | null
  onSelectBucket: (bucket: 'errors' | 'notices') => void
}

const HealthSummaryPanel = ({
  locations,
  activeBucket,
  onSelectBucket,
}: Props) => {
  const [css] = useStyletron()

  const counts = useMemo(() => {
    let healthy = 0
    let errors = 0
    let notices = 0
    for (const loc of locations) {
      if (loc.locationStatus.kind === 'errors') errors += 1
      else if (loc.locationStatus.kind === 'notices') notices += 1
      else healthy += 1
    }
    return { healthy, errors, notices, total: locations.length }
  }, [locations])

  const handleSelectErrors = useCallback(
    () => onSelectBucket('errors'),
    [onSelectBucket]
  )
  const handleSelectNotices = useCallback(
    () => onSelectBucket('notices'),
    [onSelectBucket]
  )

  if (counts.total === 0) return null

  // Tie the region's accessible name to the visible heading so SR users
  // and sighted users hear/see the same thing, including the live total.
  // Drops the previous static `aria_label` ("Location health summary")
  // in favour of the more informative dynamic title. The id lives on a
  // wrapper because @thanx/uikit's Text component types don't accept
  // arbitrary DOM props.
  return (
    <section className="mb-m" aria-labelledby={HEADING_ID}>
      <div id={HEADING_ID}>
        <Text.Header4 className={`mb-s ${css({ color: palette.grey90 })}`}>
          {t('title', { total: counts.total })}
        </Text.Header4>
      </div>
      <div className="d-flex" style={{ gap: '16px' }}>
        <Tile
          bucket="healthy"
          count={counts.healthy}
          label={t('label_healthy')}
        />
        <Tile
          bucket="errors"
          count={counts.errors}
          label={t('label_errors')}
          isSelected={activeBucket === 'errors'}
          onClick={handleSelectErrors}
        />
        <Tile
          bucket="notices"
          count={counts.notices}
          label={t('label_notices')}
          isSelected={activeBucket === 'notices'}
          onClick={handleSelectNotices}
        />
      </div>
    </section>
  )
}

export default HealthSummaryPanel
